import React, { useState } from 'react';
import {
  GitCommit,
  Play,
  Pause,
  AlertOctagon,
  CheckCircle2,
  Clock,
  ShieldCheck,
  Send,
  Users,
  Eye,
  Mail,
  Bell,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import { INITIAL_CAMPAIGN_JOURNEYS } from '../../data/growthData';
import { CampaignJourney } from '../../types';

export const CampaignOrchestratorView: React.FC = () => {
  const [journeys, setJourneys] = useState<CampaignJourney[]>(INITIAL_CAMPAIGN_JOURNEYS);
  const [selectedJourney, setSelectedJourney] = useState<CampaignJourney>(INITIAL_CAMPAIGN_JOURNEYS[0]);
  const [globalKillSwitchActive, setGlobalKillSwitchActive] = useState<boolean>(false);

  const toggleJourneyStatus = (id: string) => {
    setJourneys(prev => prev.map(j => {
      if (j.id === id) {
        const nextStatus = j.status === 'RUNNING' ? 'PAUSED' : 'RUNNING';
        return { ...j, status: nextStatus };
      }
      return j;
    }));
    if (selectedJourney.id === id) {
      setSelectedJourney(prev => ({ ...prev, status: prev.status === 'RUNNING' ? 'PAUSED' : 'RUNNING' }));
    }
  };

  const toggleGlobalKillSwitch = () => {
    setGlobalKillSwitchActive(!globalKillSwitchActive);
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-900">
              P12.3 — Campaign Orchestration Engine
            </span>
            <span className="flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              Automated Safety Guards Active
            </span>
          </div>
          <h2 className="text-xl font-black text-slate-900 tracking-tight">
            Campaign Journey Builder & Lifecycle Orchestrator
          </h2>
          <p className="text-xs text-slate-600 mt-1 max-w-3xl">
            Orchestrates automated user journeys across Email, Push, and In-App notifications. Every node enforces Consent Checks, Suppression Checks, Frequency Caps, and Quiet Hours before dispatch.
          </p>
        </div>

        {/* Global Kill Switch Button */}
        <button
          onClick={toggleGlobalKillSwitch}
          className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 cursor-pointer shadow-xs ${
            globalKillSwitchActive
              ? 'bg-rose-700 text-white hover:bg-rose-800'
              : 'bg-slate-100 text-slate-800 hover:bg-slate-200 border border-slate-300'
          }`}
        >
          <AlertOctagon className="w-4 h-4 text-current" />
          <span>{globalKillSwitchActive ? 'EMERGENCY KILL SWITCH ACTIVE' : 'Global Kill Switch (Standby)'}</span>
        </button>
      </div>

      {globalKillSwitchActive && (
        <div className="p-4 bg-rose-600 text-white rounded-xl shadow-md flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertOctagon className="w-6 h-6 shrink-0" />
            <div>
              <h4 className="text-sm font-bold">EMERGENCY CAMPAIGN KILL SWITCH IS ENGAGED</h4>
              <p className="text-xs text-rose-100 mt-0.5">
                All automated email dispatches, push notifications, and lifecycle journeys have been instantly paused globally.
              </p>
            </div>
          </div>
          <button
            onClick={toggleGlobalKillSwitch}
            className="px-3.5 py-1.5 bg-white text-rose-700 rounded-lg text-xs font-bold hover:bg-rose-50 cursor-pointer"
          >
            Disengage Kill Switch
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Journeys List */}
        <div className="lg:col-span-1 space-y-3">
          <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
            Automated Journeys ({journeys.length})
          </h3>

          <div className="space-y-2">
            {journeys.map((jny) => {
              const isSelected = selectedJourney.id === jny.id;
              const isRunning = jny.status === 'RUNNING' && !globalKillSwitchActive;
              return (
                <div
                  key={jny.id}
                  onClick={() => setSelectedJourney(jny)}
                  className={`p-4 rounded-xl border transition-all cursor-pointer ${
                    isSelected
                      ? 'bg-purple-50/50 border-purple-600 ring-1 ring-purple-600 shadow-xs'
                      : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="font-bold text-xs text-slate-900">{jny.name}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                      isRunning ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                    }`}>
                      {isRunning ? 'RUNNING' : 'PAUSED'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 line-clamp-2 mb-2">
                    {jny.objective}
                  </p>
                  <div className="flex items-center justify-between text-[11px] text-slate-500 pt-2 border-t border-slate-100">
                    <span className="font-semibold text-slate-700">
                      Target: {jny.targetSegmentName}
                    </span>
                    <span className="text-slate-400 font-mono text-[10px]">
                      {jny.category}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Selected Journey Visual Node Pipeline & Metrics */}
        <div className="lg:col-span-2 space-y-6">
          {selectedJourney && (
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs space-y-5">
              <div className="flex items-start justify-between pb-4 border-b border-slate-100">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-bold text-slate-900">{selectedJourney.name}</h3>
                    <span className="px-2 py-0.5 rounded text-xs font-mono bg-slate-100 text-slate-700 border border-slate-200">
                      {selectedJourney.campaignKey}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">{selectedJourney.objective}</p>
                </div>

                <button
                  onClick={() => toggleJourneyStatus(selectedJourney.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 cursor-pointer ${
                    selectedJourney.status === 'RUNNING'
                      ? 'bg-rose-100 text-rose-800 hover:bg-rose-200'
                      : 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                  }`}
                >
                  {selectedJourney.status === 'RUNNING' ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                  <span>{selectedJourney.status === 'RUNNING' ? 'Pause Journey' : 'Resume Journey'}</span>
                </button>
              </div>

              {/* Journey Metrics Dashboard */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <span className="text-slate-500 block text-[11px]">Eligible Audience</span>
                  <span className="font-bold text-slate-900 text-sm">
                    {selectedJourney.metrics.eligibleUsers.toLocaleString()}
                  </span>
                </div>
                <div className="p-3 bg-rose-50 rounded-lg border border-rose-200">
                  <span className="text-rose-700 block text-[11px]">Suppressed / Guarded</span>
                  <span className="font-bold text-rose-900 text-sm">
                    {selectedJourney.metrics.suppressedUsersCount} users
                  </span>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <span className="text-emerald-700 block text-[11px]">Successful Dispatches</span>
                  <span className="font-bold text-emerald-900 text-sm">
                    {selectedJourney.metrics.sentCount.toLocaleString()}
                  </span>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <span className="text-blue-700 block text-[11px]">Useful Value Activated</span>
                  <span className="font-bold text-blue-900 text-sm">
                    {selectedJourney.metrics.activatedUsersCount.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Journey Node Visual Flow */}
              <div className="pt-2 space-y-3">
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                  <GitCommit className="w-3.5 h-3.5 text-purple-900" />
                  Visual Journey Nodes & Automated Safety Guards
                </h4>

                <div className="space-y-2">
                  {selectedJourney.journeyNodes.map((node, index) => (
                    <div key={node.stepNumber} className="relative">
                      {index > 0 && (
                        <div className="w-0.5 h-3 bg-slate-300 mx-auto my-0.5" />
                      )}
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between gap-3 text-xs">
                        <div className="flex items-center gap-2.5">
                          <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-900 font-bold flex items-center justify-center shrink-0">
                            {node.stepNumber}
                          </span>
                          <div>
                            <span className="font-bold text-slate-900 block">{node.label}</span>
                            <span className="text-slate-500 text-[11px]">{node.details}</span>
                          </div>
                        </div>

                        <span className="px-2.5 py-0.5 rounded text-[10px] font-bold bg-slate-200 text-slate-700 shrink-0 font-mono">
                          {node.type}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
